import { Card, CardText, Button } from "react-bootstrap";
import useImagine, { IMAGINE_TYPES } from "./useImagine";
import { useEffect, useState } from "react";

function useEstimatedProgress(seconds = 45) {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => {
      const t = (Date.now() - start) / 1000;
      setPct(Math.min(95, Math.round(95 * (1 - Math.exp(-t / seconds)))));
    }, 500);
    return () => clearInterval(id);
  }, [seconds]);
  return pct;
}

function AnimatingOverlay() {
  const pct = useEstimatedProgress(45);
  return (
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, height: '75%',
      zIndex: 2,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: '1rem',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
    }}>
      <span style={{
        color: 'silver', fontSize: '1.1rem',
        letterSpacing: '0.12em', textTransform: 'uppercase',
      }}>
        <h11>
          Animating
        </h11>
      </span>
      <svg
        width="110" height="110" viewBox="0 0 100 100" aria-hidden="true"
        style={{ filter: 'drop-shadow(0 0 6px rgba(243, 61, 61, 0.8))' }}
      >
        <circle
          cx="50" cy="50" r="38"
          fill="none"
          stroke="rgba(243, 61, 61, 0.9)"
          strokeWidth="9"
          strokeLinecap="butt"
          strokeDasharray="50 29.587"
        >
          <animateTransform
            attributeName="transform" type="rotate"
            from="0 50 50" to="360 50 50"
            dur="1.1s" repeatCount="indefinite"
          />
        </circle>
        <text
          x="50" y="50"
          textAnchor="middle" dominantBaseline="central"
          fill="silver" fontSize="20"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {pct}%
        </text>
      </svg>
    </div>
  );
}

export default function FlipCard({ src, alt, name, role, description, character }) {
  const im = useImagine(character);
  const working = im.phase === "working";
  const busy = working || im.videoPhase === "working";

  const mediaStyle = {
    width: '100%', height: '75%', objectFit: 'cover',
    borderRadius: '12px 12px 0 0', display: 'block',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div className="card-wrapper" style={{ perspective: '1000px' }}>
        <div
          style={{
            width: '280px',
            height: '440px',
            position: 'relative',
            transformStyle: 'preserve-3d',
            transition: 'transform 0.7s ease',
            cursor: 'pointer',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'rotateY(180deg)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'rotateY(0deg)'}
        >
          {/* FRONT */}
          <div style={{
            position: 'absolute', inset: 0,
            backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
           backgroundColor: 'rgba(20, 19, 19, 0.95)',
transform: 'rotateY(0deg)',
            borderRadius: "30px", overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.03)',
            boxShadow: `
              0 2px 0 rgba(255, 255, 255, 0.03) inset,
              0 -2px 0 rgba(0, 0, 0, 0.5) inset,
              0 12px 24px rgba(0, 0, 0, 0.6),
              0 32px 64px rgba(0, 0, 0, 0.5),
              0 64px 100px rgba(0, 0, 0, 0.35)
            `,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>
            {im.videoUrl ? (
              <video src={im.videoUrl} autoPlay loop muted playsInline style={mediaStyle} />
            ) : (
              <img src={im.result || src} alt={alt} style={mediaStyle} />
            )}

            {/* Animating overlay: must stay inside this FRONT div */}
                       {im.videoPhase === 'working' && <AnimatingOverlay />}

            <div style={{ padding: '0.6rem', textAlign: 'center' }}>
              <h11 style={{ borderBottom: '3px solid rgba(92, 9, 9, 0.9)' }}>{name}</h11>
              <h11>
                {role && <div>{role}</div>}
              </h11>
            </div>
          </div>

          {/* BACK */}
          <div style={{
            position: 'absolute', inset: 0,
            backgroundColor: 'rgba(20, 19, 19, 0.95)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            // backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            borderRadius: "30px",
            border: '1px solid rgba(255, 255, 255, 0.03)',
            boxShadow: `
              0 2px 0 rgba(255, 255, 255, 0.03) inset,
              0 -2px 0 rgba(0, 0, 0, 0.5) inset,
              0 12px 24px rgba(0, 0, 0, 0.6),
              0 32px 64px rgba(0, 0, 0, 0.5),
              0 64px 100px rgba(0, 0, 0, 0.35)
            `,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', textAlign: 'center',
          }}>
            <div style={{ width: '40px', height: '2px', backgroundColor: 'rgba(243, 61, 61, 0.7)', margin: '0.3rem 0' }} />
            <CardText style={{ padding: '0.6rem', textAlign: 'left' }}>{description}</CardText>
            <div style={{ width: '40px', height: '2px', backgroundColor: 'rgba(243, 61, 61, 0.7)', margin: '0.3rem 0' }} />
          </div>
        </div>
      </div>

      {character && (
        <div style={{ width: 320, marginTop: '2rem', textAlign: 'center', color: 'white' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            {/* Step 1: no photo yet, so only Upload Photo */}
            {im.phase !== 'done' && !im.file && (
              <Button
                as="label"
                // size="sm"
                variant="outline-secondary"
                 className="process-card-text hover-brighten"
                      style={{
                        fontSize: '2.5vmin',
                        backgroundColor: 'rgb(32, 30, 30)',
                        border: '1px solid rgba(255, 255, 255, 0.03)',
                        boxShadow: `
                          0 2px 0 rgba(255, 255, 255, 0.03) inset,
                          0 -2px 0 rgba(0, 0, 0, 0.5) inset,
                          0 12px 24px rgba(0, 0, 0, 0.6),
                          0 32px 64px rgba(0, 0, 0, 0.5),
                          0 64px 100px rgba(0, 0, 0, 0.35)
                        `,
                        borderRadius: '999px',
                        cursor: 'pointer',
                      }}
              >
                <h11>
                  Upload Photo
                </h11>
                
                <input
                  type="file"
                  hidden
                  accept={IMAGINE_TYPES.join(',')}
                  onChange={im.pick}
                />
              </Button>
            )}

            {/* Step 2: photo chosen, so Upload Photo goes away and Generate takes its place */}
            {im.phase !== 'done' && im.file && (
              <Button
                // size="sm"
                variant="danger"
                disabled={working}
                onClick={im.generate}
                 className="process-card-text hover-brighten"
        style={{
          fontSize: '2.5vmin',
          backgroundColor: 'rgb(32, 30, 30)',
                        border: '1px solid rgba(255, 255, 255, 0.03)',
                        boxShadow: `
                          0 2px 0 rgba(255, 255, 255, 0.03) inset,
                          0 -2px 0 rgba(0, 0, 0, 0.5) inset,
                          0 12px 24px rgba(0, 0, 0, 0.6),
                          0 32px 64px rgba(0, 0, 0, 0.5),
                          0 64px 100px rgba(0, 0, 0, 0.35)
                        `,
                    borderRadius: '999px',
          cursor: 'pointer',
        }}
              >
                <h11>
                  {working ? im.message : 'Generate'}
                </h11>
                
              </Button>
            )}

            {/* Step 3: image ready */}
            {im.phase === 'done' && (im.videoPhase === 'idle' || im.videoPhase === 'error') && (
              <Button
                // size="sm"
                variant="outline-light"
                onClick={im.makeVideo}
                 className="process-card-text hover-brighten"
        style={{
          fontSize: '2.5vmin',
          backgroundColor: 'rgb(32, 30, 30)',
                        border: '1px solid rgba(255, 255, 255, 0.03)',
                        boxShadow: `
                          0 2px 0 rgba(255, 255, 255, 0.03) inset,
                          0 -2px 0 rgba(0, 0, 0, 0.5) inset,
                          0 12px 24px rgba(0, 0, 0, 0.6),
                          0 32px 64px rgba(0, 0, 0, 0.5),
                          0 64px 100px rgba(0, 0, 0, 0.35)
                        `,
                    borderRadius: '999px',
          cursor: 'pointer',
        }}
              >
                <h11>
                  Bring to Life
                </h11>
              </Button>
            )}

            {busy && (
              <Button
                // size="sm"
                variant="outline-light"
                onClick={im.cancel}
              >
                Cancel
              </Button>
            )}
          </div>

          {im.file && im.phase !== 'working' && im.phase !== 'done' && (
            <>
              <small style={{ display: 'block', marginTop: '0.4rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {im.file.name}
              </small>
              <CardText style={{ marginTop: '0.2rem', fontSize: '0.8rem', opacity: 0.7 }}>
                <Button variant="link" onClick={im.restore}>Change Photo</Button>
              </CardText>
            </>
          )}
          {im.phase === 'error' && <small style={{ display: 'block', marginTop: '0.4rem' }}>{im.message}</small>}
          {im.videoPhase === 'error' && <small style={{ display: 'block', marginTop: '0.4rem' }}>{im.videoMessage}</small>}

          {(im.result || im.videoUrl) && !busy && (
            <CardText style={{ marginTop: '0.4rem', fontSize: '0.8rem', opacity: 0.7 }}>
              <Button
                variant="link"
                // size="sm"
                onClick={im.restore}
              >
                Restore Original
              </Button>
            </CardText>
          )}
        </div>
      )}
    </div>
  );
}